// Hook de cambios sin guardar de un formulario: compara lo guardado con lo que hay en pantalla y lo
// anota en el registro global para que <BarraCambios> lo cuente, pregunte al salir y guarde con Ctrl+S.
import { useCallback, useEffect, useId, useMemo, useRef } from 'react';

import { type Cambio, type CampoCambio, diferencias, quitar, registrar } from './cambiosPendientes';

export type OpcionesCambios<T> = {
  titulo: string;
  inicial: T;
  actual: T;
  campos: CampoCambio<T>[];
  // Pone valores en el formulario (para Deshacer y Descartar).
  poner: (parcial: Partial<T>) => void;
  // Guarda con la logica que ya tiene el formulario; true si quedo guardado.
  guardar: () => Promise<boolean> | boolean;
  // Motivo por el que aun no se puede guardar (campo invalido).
  invalido?: string | null;
  // false mientras el formulario no aplica (cargando, modo sin perfil...).
  activo?: boolean;
};

export type CambiosPendientes = {
  cambios: Cambio[];
  n: number;
  sucio: boolean;
  cambiado: (id: string) => Cambio | undefined;
  // Clase para la fila del campo: marca ambar a la izquierda cuando cambio.
  clase: (id: string, extra?: string) => string;
  deshacer: (id: string) => void;
  descartar: () => void;
};

export function useCambiosPendientes<T>(o: OpcionesCambios<T>): CambiosPendientes {
  const id = useId();
  const activo = o.activo ?? true;
  const cambios = useMemo(() => (activo ? diferencias(o.inicial, o.actual, o.campos) : []), [activo, o.inicial, o.actual, o.campos]);
  // Las funciones cambian en cada render; el registro llama siempre a las ultimas.
  const ultimo = useRef(o);
  ultimo.current = o;

  const deshacer = useCallback((campo: string) => {
    const { inicial, poner } = ultimo.current;
    poner({ [campo]: inicial[campo as keyof T] } as Partial<T>);
  }, []);
  const descartar = useCallback(() => {
    const { inicial, poner, campos } = ultimo.current;
    poner(Object.fromEntries(campos.map((c) => [c.id, inicial[c.id]])) as Partial<T>);
  }, []);

  const clave = JSON.stringify(cambios) + '|' + (o.invalido ?? '') + '|' + o.titulo;
  useEffect(() => {
    registrar({
      id,
      titulo: ultimo.current.titulo,
      cambios,
      invalido: ultimo.current.invalido ?? null,
      guardar: async () => {
        try {
          return !!(await ultimo.current.guardar());
        } catch {
          return false;
        }
      },
      descartar,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);
  useEffect(() => () => quitar(id), [id]);

  return {
    cambios,
    n: cambios.length,
    sucio: cambios.length > 0,
    cambiado: (c) => cambios.find((x) => x.id === c),
    clase: (c, extra) => [extra, 'cp-campo', cambios.some((x) => x.id === c) ? 'cp-cambiado' : ''].filter(Boolean).join(' '),
    deshacer,
    descartar,
  };
}
